import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { supabase } from "@/integrations/supabase/client";
import { ContinuousBarCalendar } from "../calendar/ContinuousBarCalendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Event {
  id: string;
  title: string;
  start: string;
  end: string;
  type: string;
}

export const PromotionTab = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [isNewEventOpen, setIsNewEventOpen] = useState(false);
  const [isEditEventOpen, setIsEditEventOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isClearAllOpen, setIsClearAllOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedDates, setSelectedDates] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [calendarHeight, setCalendarHeight] = useState(980);
  const calendarRef = useRef<HTMLDivElement>(null);
  
  // Initialize forms with default values to prevent hook issues
  const form = useForm<{
    title: string;
    type: string;
    startDate: string;
    endDate: string;
  }>({
    defaultValues: {
      title: '',
      type: '',
      startDate: '',
      endDate: ''
    }
  });
  
  const editForm = useForm<{
    title: string;
    type: string;
    startDate: string;
    endDate: string;
  }>({
    defaultValues: {
      title: '',
      type: '',
      startDate: '',
      endDate: ''
    }
  });

  // Helper function to format dates in DD MMM YYYY format
  const formatDateDisplay = useCallback((dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  // Helper function to format date for input field (YYYY-MM-DD)
  const formatDateInput = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  }, []);

  // Update calendar height dynamically
  useEffect(() => {
    const updateCalendarHeight = () => {
      if (calendarRef.current) {
        const calendarElement = calendarRef.current.querySelector('.bg-white.rounded-lg');
        if (calendarElement) {
          const actualCalendarHeight = calendarElement.scrollHeight;
          const newHeight = actualCalendarHeight + 10; // Add 10px buffer
          setCalendarHeight(newHeight);
        }
      }
    };

    // Initial measurement
    updateCalendarHeight();

    // Set up a ResizeObserver to watch for calendar size changes
    const resizeObserver = new ResizeObserver(updateCalendarHeight);
    if (calendarRef.current) {
      resizeObserver.observe(calendarRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [events]); // Re-run when events change

  // Load campaigns from Supabase on component mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('User not authenticated');
          return;
        }

        const { data, error } = await supabase
          .from('campaigns')
          .select('*')
          .eq('user_id', user.id);

        if (error) {
          throw error;
        }

        if (data && data.length > 0) {
          const campaignEvents = data.map(campaign => ({
            id: campaign.id,
            title: campaign.campaign_title,
            start: campaign.from_date,
            end: campaign.to_date,
            type: campaign.campaign_type
          }));
          
          setEvents(campaignEvents);
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        toast.error('Failed to load campaigns');
      }
    };

    fetchCampaigns();
  }, []);

  const handleExportToExcel = useCallback(() => {
    const exportData = events.map(event => ({
      'Campaign Title': event.title,
      'Campaign Type': event.type.charAt(0).toUpperCase() + event.type.slice(1),
      'Start Date': formatDateDisplay(event.start),
      'End Date': formatDateDisplay(event.end)
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Campaign Plan");
    XLSX.writeFile(wb, "campaign-plan.xlsx");
    toast.success('Campaign plan exported successfully');
  }, [events, formatDateDisplay]);

  const handleClearAllCampaigns = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to clear campaigns');
        return;
      }

      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      // Clear the events state
      setEvents([]);
      setIsClearAllOpen(false);
      
      toast.success('All campaigns have been cleared successfully');
    } catch (error) {
      console.error('Error clearing campaigns:', error);
      toast.error('Failed to clear campaigns');
    }
  };

  const handleDateSelect = useCallback((start: string, end: string) => {
    setSelectedDates({ start, end });
    form.reset({
      title: '',
      type: '',
      startDate: formatDateInput(start),
      endDate: formatDateInput(end)
    });
    setIsNewEventOpen(true);
  }, [form, formatDateInput]);

  const handleEventClick = useCallback((event: Event) => {
    setSelectedEvent(event);
    editForm.reset({
      title: event.title,
      type: event.type,
      startDate: formatDateInput(event.start),
      endDate: formatDateInput(event.end)
    });
    setIsEditEventOpen(true);
  }, [editForm, formatDateInput]);

  const handleEventMove = useCallback((eventId: string, start: string, end: string) => {
    setEvents(prevEvents => 
      prevEvents.map(event => {
        if (event.id === eventId) {
          return {
            ...event,
            start,
            end
          };
        }
        return event;
      })
    );
    toast.success('Campaign moved successfully');
  }, []);

  const handleCreateEvent = async (values: {
    title: string;
    type: string;
    startDate: string;
    endDate: string;
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to create campaigns');
        return;
      }

      // Convert date inputs to proper datetime strings
      const startDateTime = new Date(values.startDate);
      startDateTime.setHours(8, 0, 0, 0);
      
      const endDateTime = new Date(values.endDate);
      endDateTime.setHours(18, 0, 0, 0);

      const { data: campaignData, error } = await supabase
        .from('campaigns')
        .insert({
          user_id: user.id, // Now properly typed as UUID
          campaign_type: values.type,
          campaign_title: values.title,
          from_date: startDateTime.toISOString().split('T')[0],
          to_date: endDateTime.toISOString().split('T')[0]
        })
        .select();

      if (error) {
        throw error;
      }

      if (campaignData && campaignData.length > 0) {
        const newEvent: Event = {
          id: campaignData[0].id,
          title: values.title,
          start: startDateTime.toISOString(),
          end: endDateTime.toISOString(),
          type: values.type
        };
        
        setEvents(prevEvents => [...prevEvents, newEvent]);
        setIsNewEventOpen(false);
        form.reset();
        
        toast.success('Campaign added successfully');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateEvent = async (values: {
    title: string;
    type: string;
    startDate: string;
    endDate: string;
  }) => {
    if (!selectedEvent) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to update campaigns');
        return;
      }

      // Convert date inputs to proper datetime strings
      const startDateTime = new Date(values.startDate);
      startDateTime.setHours(8, 0, 0, 0);
      
      const endDateTime = new Date(values.endDate);
      endDateTime.setHours(18, 0, 0, 0);

      const { error } = await supabase
        .from('campaigns')
        .update({
          campaign_type: values.type,
          campaign_title: values.title,
          from_date: startDateTime.toISOString().split('T')[0],
          to_date: endDateTime.toISOString().split('T')[0]
        })
        .eq('id', selectedEvent.id)
        .eq('user_id', user.id); // Ensure user can only update their own campaigns

      if (error) {
        throw error;
      }

      setEvents(prevEvents =>
        prevEvents.map(event => {
          if (event.id === selectedEvent.id) {
            return {
              ...event,
              title: values.title,
              type: values.type,
              start: startDateTime.toISOString(),
              end: endDateTime.toISOString()
            };
          }
          return event;
        })
      );
      
      setIsEditEventOpen(false);
      editForm.reset();
      
      toast.success('Campaign updated successfully');
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Failed to update campaign');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to delete campaigns');
        return;
      }

      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', selectedEvent.id)
        .eq('user_id', user.id); // Ensure user can only delete their own campaigns

      if (error) {
        throw error;
      }

      setEvents(prevEvents => prevEvents.filter(event => event.id !== selectedEvent.id));
      setIsEditEventOpen(false);
      setIsDeleteDialogOpen(false);
      
      toast.success('Campaign deleted successfully');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[12px] text-secondary-light">
        Plan the integrated marketing campaign activities used to market the product or service, such as communications, advertising, PR, marketing events and campaign promotions.
      </p>
      <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-3" style={{ height: `${calendarHeight + 100}px` }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Campaign Management</h2>
          <div className="flex gap-2">
            <Button onClick={handleExportToExcel}>Save plan</Button>
            <AlertDialog open={isClearAllOpen} onOpenChange={setIsClearAllOpen}>
              <AlertDialogTrigger asChild>
                <Button className="bg-destructive gap-2 bg-red-500 text-black hover:bg-red-400">Clear all campaigns</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear All Campaigns</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to clear all campaigns? This action will permanently delete all your campaign records and cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive gap-2 bg-red-500 text-black hover:bg-red-400" onClick={handleClearAllCampaigns}>
                    Clear All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        
        <div ref={calendarRef} style={{ height: `${calendarHeight}px` }}>
          <ContinuousBarCalendar
            events={events}
            onDateSelect={handleDateSelect}
            onEventClick={handleEventClick}
            onEventMove={handleEventMove}
          />
        </div>

        {/* Add New Campaign Activity Dialog */}
        <Dialog open={isNewEventOpen} onOpenChange={setIsNewEventOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Campaign Activity</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleCreateEvent)} className="space-y-4">
                <FormField 
                  control={form.control} 
                  name="type" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Campaign Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="campaign-select-trigger bg-white text-black">
                            <SelectValue placeholder="Select campaign type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="select-content">
                          <SelectItem value="advertising">Advertising</SelectItem>
                          <SelectItem value="pr">Public Relations</SelectItem>
                          <SelectItem value="promotion">Marketing Promotion</SelectItem>
                          <SelectItem value="event">Event (online/offline)</SelectItem>
                          <SelectItem value="social">Social Media</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} 
                />
                <FormField 
                  control={form.control} 
                  name="title" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Campaign Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )} 
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField 
                    control={form.control} 
                    name="startDate" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>From Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                      </FormItem>
                    )} 
                  />
                  <FormField 
                    control={form.control} 
                    name="endDate" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>To Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                      </FormItem>
                    )} 
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit">Add Activity</Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Edit Campaign Activity Dialog */}
        <Dialog open={isEditEventOpen} onOpenChange={setIsEditEventOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Campaign Activity</DialogTitle>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(handleUpdateEvent)} className="space-y-4">
                <FormField 
                  control={editForm.control} 
                  name="type" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activity Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="campaign-select-trigger bg-white text-black">
                            <SelectValue placeholder="Select activity type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="select-content">
                          <SelectItem value="advertising">Advertising</SelectItem>
                          <SelectItem value="pr">Public Relations</SelectItem>
                          <SelectItem value="promotion">Marketing Promotion</SelectItem>
                          <SelectItem value="event">Event (online/offline)</SelectItem>
                          <SelectItem value="social">Social Media</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} 
                />
                <FormField 
                  control={editForm.control} 
                  name="title" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activity Title</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )} 
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField 
                    control={editForm.control} 
                    name="startDate" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>From Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                      </FormItem>
                    )} 
                  />
                  <FormField 
                    control={editForm.control} 
                    name="endDate" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>To Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                      </FormItem>
                    )} 
                  />
                </div>
                <div className="flex justify-between">
                  <Button 
                    type="button" 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    Delete Activity
                  </Button>
                  <Button type="submit">Update Campaign</Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete this campaign activity? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteEvent}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
